// Branded id types — opaque strings that can only be produced via the smart
// constructors below. Keeps `AccountId` and `RepoId` from being interchanged
// at call sites.

import { z } from 'zod';

const idPattern = /^[a-z]{3}_[A-Za-z0-9_-]{8,}$/;

/**
 * Every schema's prefix, keyed by the schema itself.
 *
 * Populated by {@link make} and read only by {@link mintId}, which is the whole
 * point: a minter that took the prefix as its own argument would state it a
 * second time, and the day the two disagree the mint produces ids its own
 * schema rejects. Here the prefix is written once, in the `make` call, and the
 * minter cannot reach a different one.
 */
const prefixOf = new WeakMap<object, string>();

const make = <Brand extends string>(prefix: string) => {
  const schema = z
    .string()
    .regex(idPattern, `invalid id: expected /${idPattern.source}/`)
    .refine((v) => v.startsWith(`${prefix}_`), `expected prefix "${prefix}_"`)
    .brand<Brand>();
  prefixOf.set(schema, prefix);
  return schema;
};

/**
 * Mint a fresh id for `schema`, branded as that schema's type.
 *
 * The one minter. Before it, 48 call sites across eight `apps/worker`
 * directories carried the identical expression
 * `SessionIdSchema.parse(\`ses_${crypto.randomUUID().replaceAll('-', '')}\`)`,
 * and THREE lanes had independently reinvented the same local `newSessionId`
 * helper around it — the shape that says a rule cannot reach every lane while
 * every lane owns a copy.
 *
 * Two things it deletes beyond the duplication:
 *
 * - **the redundant `parse`.** Those sites validated a string they had just
 *   built from a literal one line above. It never failed and never could; it
 *   only cost a regex and a refinement per mint.
 * - **the chance of a prefix typo.** `ses_`/`sess_` and a transposed brand are
 *   both a compile error now rather than a row written under an id nothing
 *   queries.
 *
 * A caller that wants a DIFFERENT shape under the same prefix must keep writing
 * it by hand, and one does: `apps/worker`'s blog lane mints `evt_job_<uuid>`,
 * which is a valid `AuditEventId` with a `job_` infix. Routing it through here
 * would silently restore it to the plain shape, and an id scheme already in the
 * database is a one-way door.
 */
export function mintId<Schema extends z.ZodTypeAny>(schema: Schema): z.infer<Schema> {
  const prefix = prefixOf.get(schema);
  if (prefix === undefined) {
    // Only reachable by passing a schema not built by `make` — the branded id
    // types are the closed set this minter serves.
    throw new Error('mintId: schema was not built by make() and has no id prefix');
  }
  return `${prefix}_${crypto.randomUUID().replaceAll('-', '')}` as z.infer<Schema>;
}

export const AccountIdSchema = make<'AccountId'>('acc');
export const InstallationIdSchema = make<'InstallationId'>('ins');
export const RepoIdSchema = make<'RepoId'>('rep');
/**
 * A repository id AS STORED: `repositories.id` is `uuid DEFAULT gen_random_uuid()`, so no row
 * holds the `rep_` shape {@link RepoIdSchema} demands (`packages/db` `id-brand-parity.test.ts`
 * lists the table as uuid-backed). Same brand, so it flows wherever a `RepoId` does. A payload
 * that carries a repository row's id across a queue parses it with this one: the `rep_` shape
 * there made every `gitlab.policy_ingest` job built from a real row throw (#7931), the same
 * defect `StoredBYOKKeyIdSchema` closed for key ids.
 */
export const StoredRepoIdSchema = z.uuid().brand<'RepoId'>();
export const PolicyIdSchema = make<'PolicyId'>('pol');
export const IssueIdSchema = make<'IssueId'>('iss');
export const PullRequestIdSchema = make<'PullRequestId'>('prq');
export const SessionIdSchema = make<'SessionId'>('ses');
export const AuditEventIdSchema = make<'AuditEventId'>('evt');
export const EscalationIdSchema = make<'EscalationId'>('esc');
export const BYOKKeyIdSchema = make<'BYOKKeyId'>('byk');
/**
 * A BYOK key id AS STORED: `byok_keys.id` is `uuid DEFAULT gen_random_uuid()`, and
 * no row has ever held the `byk_` shape {@link BYOKKeyIdSchema} demands (92/92 prod
 * rows plain uuids, `infra psql`, 2026-09-28). Same brand, so it flows wherever a
 * `BYOKKeyId` does. EVERY schema that parses a key id read back from the database
 * — `BYOKKeySchema.id`, the `key_outages` snapshot's `keyId` — uses this one:
 * the `byk_` shape there made `cron.key_outage` throw on every run for an account
 * with an open outage (GlitchTip #111). Retiring the `byk_` schema for this one is
 * #145; the api's key routes already validate the uuid at the edge (#179).
 */
export const StoredBYOKKeyIdSchema = z.uuid().brand<'BYOKKeyId'>();
export const WebhookIdSchema = make<'WebhookId'>('wbk');
/** A CONNECTED webhook source (arc 101 doc 16) — the routing key in `POST /hooks/:source_id`. */
export const WebhookSourceIdSchema = make<'WebhookSourceId'>('whs');
export const WebhookBindingIdSchema = make<'WebhookBindingId'>('wbb');
export const EmailRouteIdSchema = make<'EmailRouteId'>('eml');
export const TaskIdSchema = make<'TaskId'>('tsk');
/** A RAW task (arc 101 doc 01) — the admission object, distinct from `tsk_` executable work. */
export const RawTaskIdSchema = make<'RawTaskId'>('rtk');
/** A BLOCKER (arc 101 slice 11) — the durable, answerable "a human is owed" object. */
export const BlockerIdSchema = make<'BlockerId'>('blk');
/** A MODEL-CALL OFFER (#6759, `model_call_jobs`) — `mcj_<32 hex>`, minted by the column default. */
export const ModelCallJobIdSchema = make<'ModelCallJobId'>('mcj');
/**
 * A STAFF BUG REPORT (plan 101 slice 05, `platform_bugs`) — `bug_<32 hex>`, minted by
 * the column default. Its own prefix: a `duplicate_of` names one, and a raw task or a
 * task id handed there must not parse.
 */
export const PlatformBugIdSchema = make<'PlatformBugId'>('bug');
/**
 * A SCOUT FAN-OUT (#3359) — the grouping every sub-scout of one multi-repo ask
 * carries on its envelope, and the address its merged answer is read by.
 *
 * Its own prefix rather than a reuse of `tsk`: a fan-out is not a task and has no
 * row of its own — it exists only as a value repeated across its members' envelopes,
 * so a caller holding one must not be able to hand it to a verb that takes a task id
 * and have it parse.
 */
export const ScoutFanoutIdSchema = make<'ScoutFanoutId'>('fan');
export const TemplateIdSchema = make<'TemplateId'>('tpl');
export const IntegrationIdSchema = make<'IntegrationId'>('int');
export const PromptVarIdSchema = make<'PromptVarId'>('pvr');
/**
 * An INVITATION (arc 101 slice 04) — the pre-identity grant object. Its own
 * prefix rather than a reuse of `usr`/`acc`: an invitation is handed to a
 * transport as an opaque id beside those two, and a shared namespace is how one
 * gets passed where another is meant.
 */
export const InvitationIdSchema = make<'InvitationId'>('inv');
/**
 * An ACCESS REQUEST (arc 101 slice 01) — the application that PRECEDES an
 * invitation. Its own prefix for the same reason `inv` has one: it is handed to
 * the admin transports beside `inv_` and `usr_` ids, and the invite action takes
 * one of each.
 */
export const AccessRequestIdSchema = make<'AccessRequestId'>('acr');
export const DashboardIdSchema = make<'DashboardId'>('dsh');
export const DashboardWidgetIdSchema = make<'DashboardWidgetId'>('dwg');
/**
 * An INCIDENT (#4598) — the handle a customer quotes for ONE unexpected throw
 * behind a surface that never tells them what threw (`packages/mcp`'s
 * `unexpectedFailure`). It addresses no row: the record it points at is the
 * error report and the log line the same mint rides on, so the id exists purely
 * to join a support thread to that report.
 *
 * Its own prefix rather than a reuse of `evt`: an `evt_` id is an AUDIT EVENT a
 * caller can fetch, and handing a support handle to a verb that takes one must
 * not parse.
 */
export const IncidentIdSchema = make<'IncidentId'>('inc');
/**
 * A VISIBILITY EPISODE (03-5) — one public-to-private trip of a repo, the tail of its
 * `repo.requires_upgrade` notice's dedupe key and the row's `input.episode`. It
 * addresses no row. Its own prefix rather than a reuse of `ses`: it is not a session,
 * and a `ses_` value reads as one an operator could look up in `agent_sessions`.
 */
export const VisibilityEpisodeIdSchema = make<'VisibilityEpisodeId'>('vep');

export type AccountId = z.infer<typeof AccountIdSchema>;
export type InstallationId = z.infer<typeof InstallationIdSchema>;
export type RepoId = z.infer<typeof RepoIdSchema>;
export type PolicyId = z.infer<typeof PolicyIdSchema>;
export type IssueId = z.infer<typeof IssueIdSchema>;
export type PullRequestId = z.infer<typeof PullRequestIdSchema>;
export type SessionId = z.infer<typeof SessionIdSchema>;
export type AuditEventId = z.infer<typeof AuditEventIdSchema>;
export type EscalationId = z.infer<typeof EscalationIdSchema>;
export type BYOKKeyId = z.infer<typeof BYOKKeyIdSchema>;
export type WebhookId = z.infer<typeof WebhookIdSchema>;
export type WebhookSourceId = z.infer<typeof WebhookSourceIdSchema>;
export type WebhookBindingId = z.infer<typeof WebhookBindingIdSchema>;
export type EmailRouteId = z.infer<typeof EmailRouteIdSchema>;
export type TaskId = z.infer<typeof TaskIdSchema>;
export type RawTaskId = z.infer<typeof RawTaskIdSchema>;
export type BlockerId = z.infer<typeof BlockerIdSchema>;
export type ModelCallJobId = z.infer<typeof ModelCallJobIdSchema>;
export type PlatformBugId = z.infer<typeof PlatformBugIdSchema>;
export type TemplateId = z.infer<typeof TemplateIdSchema>;
export type IntegrationId = z.infer<typeof IntegrationIdSchema>;
export type PromptVarId = z.infer<typeof PromptVarIdSchema>;
export type InvitationId = z.infer<typeof InvitationIdSchema>;
export type AccessRequestId = z.infer<typeof AccessRequestIdSchema>;
export type DashboardId = z.infer<typeof DashboardIdSchema>;
export type DashboardWidgetId = z.infer<typeof DashboardWidgetIdSchema>;
export type IncidentId = z.infer<typeof IncidentIdSchema>;
export type VisibilityEpisodeId = z.infer<typeof VisibilityEpisodeIdSchema>;
